import { uiManager } from './UIManager';
import { listProfiles, saveProfile, setProfileActive, type ClientProfile } from '../api/profiles';

type AdminTab = 'sessions' | 'profiles';

function escapeHtml(value: any): string {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    }[char] || char));
}

function normalizeSlug(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-')
        .slice(0, 80);
}

export class AdminPortal {
    private element: HTMLElement;
    private apiUrl = import.meta.env.VITE_API_URL || '';
    private token = localStorage.getItem('ais_admin_token') || '';
    private isLocalAdmin = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
        || this.apiUrl.includes('localhost')
        || this.apiUrl.includes('127.0.0.1');
    private activeTab: AdminTab = 'sessions';
    private profiles: ClientProfile[] = [];
    private selectedProfile: ClientProfile | null = null;

    constructor() {
        this.element = document.createElement('div');
        this.element.className = 'admin-portal';
        this.element.style.padding = '2rem';
        this.element.style.minHeight = '100vh';
        this.element.style.background = 'var(--bg-100)';
    }

    public async mount() {
        this.renderShell();
        await this.loadActiveTab();
    }

    private renderShell() {
        const authControls = this.isLocalAdmin ? `
                    <div style="color: var(--color-text-muted); border: 1px solid var(--border-color); border-radius: 6px; padding: 0.65rem 0.75rem;">Local admin access</div>
                    <button id="close-admin" class="btn btn-outline">Exit Portal</button>
                ` : `
                    <input id="admin-token" type="password" placeholder="Admin token" value="${escapeHtml(this.token)}" style="background: var(--bg-200); color: white; border: 1px solid var(--border-color); border-radius: 6px; padding: 0.65rem 0.75rem; width: 240px;" />
                    <button id="save-token" class="btn btn-outline">Use Token</button>
                    <button id="close-admin" class="btn btn-outline">Exit Portal</button>
                `;

        this.element.innerHTML = `
            <div style="display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; margin-bottom: 1.5rem; flex-wrap: wrap;">
                <div>
                    <h1 style="font-family: var(--font-display); font-size: 2.5rem; color: var(--color-primary); margin: 0;">Admin Portal</h1>
                    <div style="color: var(--color-text-muted); margin-top: 0.35rem;">Sessions and client preload profiles</div>
                </div>
                <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                    ${authControls}
                </div>
            </div>

            <div style="display: flex; gap: 0.5rem; margin-bottom: 1rem; border-bottom: 1px solid var(--border-color);">
                <button id="tab-sessions" class="btn ${this.activeTab === 'sessions' ? '' : 'btn-outline'}" style="border-radius: 6px 6px 0 0;">Sessions</button>
                <button id="tab-profiles" class="btn ${this.activeTab === 'profiles' ? '' : 'btn-outline'}" style="border-radius: 6px 6px 0 0;">Client Profiles</button>
            </div>

            <div id="admin-status" style="min-height: 1.5rem; color: var(--color-text-muted); margin-bottom: 1rem;"></div>
            <div id="admin-content" style="background: var(--bg-200); border-radius: 8px; border: 1px solid var(--border-color); overflow: hidden;">
                <div style="color: var(--color-text-muted); padding: 2rem;">Loading...</div>
            </div>
        `;

        uiManager.renderScreen(this.element);

        this.element.querySelector('#close-admin')?.addEventListener('click', () => {
            window.location.hash = '';
            window.location.href = '/';
        });
        this.element.querySelector('#save-token')?.addEventListener('click', async () => {
            const input = this.element.querySelector('#admin-token') as HTMLInputElement;
            this.token = input.value.trim();
            if (this.token) {
                localStorage.setItem('ais_admin_token', this.token);
            } else {
                localStorage.removeItem('ais_admin_token');
            }
            await this.loadActiveTab();
        });
        this.element.querySelector('#tab-sessions')?.addEventListener('click', async () => {
            this.activeTab = 'sessions';
            this.renderShell();
            await this.loadActiveTab();
        });
        this.element.querySelector('#tab-profiles')?.addEventListener('click', async () => {
            this.activeTab = 'profiles';
            this.renderShell();
            await this.loadActiveTab();
        });
    }

    private setStatus(message: string, tone: 'muted' | 'error' | 'success' = 'muted') {
        const status = this.element.querySelector('#admin-status') as HTMLElement;
        if (!status) return;
        const color = tone === 'error' ? '#ff6b6b' : tone === 'success' ? '#7ee787' : 'var(--color-text-muted)';
        status.innerHTML = `<span style="color:${color};">${escapeHtml(message)}</span>`;
    }

    private authHeaders(): HeadersInit {
        return {
            'Content-Type': 'application/json',
            ...(this.token ? { 'X-Admin-Token': this.token } : {}),
        };
    }

    private async loadActiveTab() {
        if (!this.token && !this.isLocalAdmin) {
            this.setStatus('Enter the admin token to manage sessions and client profiles.');
            const content = this.element.querySelector('#admin-content');
            if (content) content.innerHTML = `<div style="padding: 2rem; color: var(--color-text-muted);">Token required.</div>`;
            return;
        }

        if (this.activeTab === 'profiles') {
            await this.loadProfiles();
        } else {
            await this.loadSessions();
        }
    }

    private async loadProfiles() {
        try {
            this.setStatus('Loading client profiles...');
            this.profiles = await listProfiles(this.token);
            if (!this.selectedProfile && this.profiles.length > 0) {
                this.selectedProfile = this.profiles[0];
            }
            this.renderProfiles();
            this.setStatus(`${this.profiles.length} client profile${this.profiles.length === 1 ? '' : 's'} loaded.`);
        } catch (error: any) {
            this.setStatus(error.message || 'Could not load profiles.', 'error');
            const content = this.element.querySelector('#admin-content');
            if (content) content.innerHTML = `<div style="padding: 2rem; color: #ff6b6b;">${escapeHtml(error.message || 'Could not load profiles.')}</div>`;
        }
    }

    private renderProfiles() {
        const content = this.element.querySelector('#admin-content');
        if (!content) return;

        const selected = this.selectedProfile || {
            slug: '',
            display_name: '',
            markdown: `# Company Overview\n\n## Known Technology Stack\n\n- \n\n## IT Restrictions / Constraints\n\n- \n\n## Strategic Context\n\n- \n`,
            is_active: true,
        };

        content.innerHTML = `
            <div style="display: grid; grid-template-columns: minmax(220px, 320px) 1fr; min-height: 640px;">
                <div style="border-right: 1px solid var(--border-color); padding: 1rem; background: rgba(0,0,0,0.12);">
                    <button id="new-profile" class="btn" style="width: 100%; margin-bottom: 1rem;">New Profile</button>
                    <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                        ${this.profiles.length === 0 ? `<div style="color: var(--color-text-muted); padding: 1rem;">No profiles yet.</div>` : this.profiles.map(profile => `
                            <button class="profile-row" data-slug="${escapeHtml(profile.slug)}" style="text-align: left; padding: 0.75rem; border-radius: 6px; border: 1px solid ${selected.slug === profile.slug ? 'var(--color-primary)' : 'var(--border-color)'}; background: ${selected.slug === profile.slug ? 'rgba(96,203,232,0.12)' : 'rgba(0,0,0,0.18)'}; color: white; cursor: pointer;">
                                <div style="font-weight: 700;">${escapeHtml(profile.display_name)}</div>
                                <div style="font-size: 0.8rem; color: var(--color-text-muted);">/${escapeHtml(profile.slug)} ${profile.is_active ? '' : '(inactive)'}</div>
                            </button>
                        `).join('')}
                    </div>
                </div>

                <div style="padding: 1rem;">
                    <div style="display: grid; grid-template-columns: 220px 1fr 120px; gap: 0.75rem; margin-bottom: 0.75rem; align-items: end;">
                        <label style="display: flex; flex-direction: column; gap: 0.35rem; color: var(--color-text-muted); font-size: 0.8rem;">
                            Slug
                            <input id="profile-slug" value="${escapeHtml(selected.slug)}" placeholder="southerncompany" style="background: var(--bg-100); color: white; border: 1px solid var(--border-color); border-radius: 6px; padding: 0.65rem;" />
                        </label>
                        <label style="display: flex; flex-direction: column; gap: 0.35rem; color: var(--color-text-muted); font-size: 0.8rem;">
                            Display Name
                            <input id="profile-name" value="${escapeHtml(selected.display_name)}" placeholder="Southern Company" style="background: var(--bg-100); color: white; border: 1px solid var(--border-color); border-radius: 6px; padding: 0.65rem;" />
                        </label>
                        <label style="display: flex; align-items: center; gap: 0.5rem; color: rgba(255,255,255,0.85); padding-bottom: 0.65rem;">
                            <input id="profile-active" type="checkbox" ${selected.is_active !== false ? 'checked' : ''} />
                            Active
                        </label>
                    </div>

                    <div id="profile-url" style="font-size: 0.85rem; color: var(--color-text-muted); margin-bottom: 0.75rem;"></div>

                    <textarea id="profile-markdown" spellcheck="false" style="width: 100%; min-height: 420px; resize: vertical; background: #08111f; color: rgba(255,255,255,0.92); border: 1px solid var(--border-color); border-radius: 6px; padding: 1rem; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; line-height: 1.5;">${escapeHtml(selected.markdown)}</textarea>

                    <div style="display: flex; gap: 0.75rem; margin-top: 1rem; align-items: center;">
                        <button id="save-profile" class="btn">Save Profile</button>
                        ${selected.slug ? `<button id="toggle-profile" class="btn btn-outline">${selected.is_active === false ? 'Activate' : 'Deactivate'}</button>` : ''}
                        <span id="profile-save-status" style="color: var(--color-text-muted);"></span>
                    </div>
                </div>
            </div>
        `;

        content.querySelector('#new-profile')?.addEventListener('click', () => {
            this.selectedProfile = null;
            this.renderProfiles();
        });
        content.querySelectorAll('.profile-row').forEach(row => {
            row.addEventListener('click', () => {
                const slug = (row as HTMLElement).dataset.slug;
                this.selectedProfile = this.profiles.find(profile => profile.slug === slug) || null;
                this.renderProfiles();
            });
        });

        const slugInput = content.querySelector('#profile-slug') as HTMLInputElement;
        const nameInput = content.querySelector('#profile-name') as HTMLInputElement;
        const urlEl = content.querySelector('#profile-url') as HTMLElement;
        const updateUrl = () => {
            const slug = normalizeSlug(slugInput.value);
            urlEl.innerHTML = slug
                ? `Public URL: <a href="/${escapeHtml(slug)}" target="_blank" style="color: var(--color-sky-blue);">${escapeHtml(window.location.origin)}/${escapeHtml(slug)}</a>`
                : 'Public URL appears after you enter a slug.';
        };
        slugInput.addEventListener('input', updateUrl);
        nameInput.addEventListener('input', () => {
            if (!slugInput.value.trim()) slugInput.value = normalizeSlug(nameInput.value);
            updateUrl();
        });
        updateUrl();

        content.querySelector('#save-profile')?.addEventListener('click', async () => {
            const markdown = (content.querySelector('#profile-markdown') as HTMLTextAreaElement).value;
            const active = (content.querySelector('#profile-active') as HTMLInputElement).checked;
            const status = content.querySelector('#profile-save-status') as HTMLElement;
            try {
                status.textContent = 'Saving...';
                const saved = await saveProfile({
                    slug: slugInput.value,
                    display_name: nameInput.value,
                    markdown,
                    is_active: active,
                }, this.token);
                this.selectedProfile = saved;
                await this.loadProfiles();
                this.setStatus(`Saved ${saved.display_name}.`, 'success');
            } catch (error: any) {
                status.textContent = error.message || 'Save failed.';
                status.style.color = '#ff6b6b';
            }
        });

        content.querySelector('#toggle-profile')?.addEventListener('click', async () => {
            if (!this.selectedProfile?.slug) return;
            await setProfileActive(this.selectedProfile.slug, this.selectedProfile.is_active === false, this.token);
            await this.loadProfiles();
        });
    }

    private async loadSessions() {
        try {
            this.setStatus('Loading active sessions...');
            const response = await fetch(`${this.apiUrl}/chat/sessions/list`, {
                headers: this.authHeaders(),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || `Session load failed: ${response.status}`);
            }
            const sessions = await response.json();
            
            const container = this.element.querySelector('#admin-content');
            if (!container) return;
            
            if (!sessions || sessions.length === 0) {
                container.innerHTML = `<div style="color: var(--color-text-muted); padding: 2rem;">No active sessions found.</div>`;
                this.setStatus('No active sessions found.');
                return;
            }

            const tableHtml = `
                <table style="width: 100%; border-collapse: collapse; text-align: left;">
                    <thead>
                        <tr style="border-bottom: 1px solid var(--border-color); background: rgba(0,0,0,0.2);">
                            <th style="padding: 1rem; font-family: var(--font-display); color: var(--color-text-muted); width: 80px;">Avatar</th>
                            <th style="padding: 1rem; font-family: var(--font-display); color: var(--color-text-muted); width: 15%;">Participant</th>
                            <th style="padding: 1rem; font-family: var(--font-display); color: var(--color-text-muted); width: 100px;">Progress</th>
                            <th style="padding: 1rem; font-family: var(--font-display); color: var(--color-text-muted); width: 30%;">Key Themes & Context</th>
                            <th style="padding: 1rem; font-family: var(--font-display); color: var(--color-text-muted);">Strategy Synopsis</th>
                            <th style="padding: 1rem; font-family: var(--font-display); color: var(--color-text-muted); width: 120px;">Last Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${sessions.map((s: any) => {
                            let cleanContext = s.company_context || '';
                            cleanContext = cleanContext.replace(/BUSINESS CONTEXT \(from [^)]+\):\n+/i, '');
                            cleanContext = cleanContext.replace(/CLIENT PROFILE \([^)]+\):\n+/i, '');
                            cleanContext = escapeHtml(cleanContext)
                                .replace(/\\n/g, '<br/>')
                                .replace(/\n/g, '<br/>');

                            const state = typeof s.session_data === 'string' ? JSON.parse(s.session_data || '{}') : (s.session_data || {});
                            let synopsisHtml = `<div style="font-size: 0.85rem; color: rgba(255,255,255,0.3); font-style: italic;">No strategy formulated yet</div>`;
                            const phase2 = state['2'];
                            const phase3 = state['3'];
                            const strategies = [];

                            if (phase3 && phase3.architecture) strategies.push(`<strong>Architecture:</strong> ${escapeHtml(phase3.architecture)}`);
                            if (phase2 && phase2.solutionOverview) strategies.push(`<strong>Solution:</strong> ${escapeHtml(phase2.solutionOverview)}`);
                            if (phase2 && phase2.hitlStrategy) strategies.push(`<strong>Human-in-the-Loop:</strong> ${escapeHtml(phase2.hitlStrategy)}`);
                            
                            if (strategies.length > 0) {
                                synopsisHtml = `<div style="font-size: 0.85rem; color: rgba(255,255,255,0.8); line-height: 1.5; max-height: 200px; overflow-y: auto;">
                                    ${strategies.map(str => `<div style="margin-bottom: 0.5rem; background: rgba(0,0,0,0.2); padding: 0.5rem; border-radius: 4px; border-left: 2px solid var(--color-accent);">${str}</div>`).join('')}
                                </div>`;
                            }

                            return `
                            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05); background: rgba(0,0,0,0.1);">
                                <td style="padding: 1.5rem 1rem; text-align: center; vertical-align: top;">
                                    ${s.has_avatar && s.avatar_data
                                        ? `<img src="${escapeHtml(s.avatar_data)}" style="width: 56px; height: 56px; border-radius: 50%; object-fit: cover; border: 2px solid var(--color-primary); box-shadow: 0 4px 6px rgba(0,0,0,0.3);" />`
                                        : `<div style="width: 56px; height: 56px; border-radius: 50%; background: var(--color-accent); display: flex; align-items: center; justify-content: center; color: white; font-size: 1.5rem; font-family: var(--font-display); font-weight: bold; margin: 0 auto; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">${escapeHtml(s.player_name).charAt(0).toUpperCase()}</div>`
                                    }
                                </td>
                                <td style="padding: 1.5rem 1rem; vertical-align: top; max-width: 200px;">
                                    <div style="font-weight: 600; font-family: var(--font-display); font-size: 1.2rem; color: #fff; margin-bottom: 0.25rem;">${escapeHtml(s.player_name)}</div>
                                    <div style="font-size: 0.9rem; color: rgba(255,255,255,0.6);">${escapeHtml(s.company_name || 'No Company')}</div>
                                </td>
                                <td style="padding: 1.5rem 1rem; vertical-align: top; max-width: 150px;">
                                    <div style="display: inline-block; padding: 0.25rem 0.5rem; background: rgba(var(--color-accent-rgb), 0.15); border: 1px solid rgba(var(--color-accent-rgb), 0.3); border-radius: 4px; font-size: 0.85rem; color: var(--color-accent); font-weight: bold; margin-bottom: 0.5rem;">Phase ${escapeHtml(s.current_phase)}</div>
                                    <div style="font-size: 0.75rem; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.05em;">Gear ${escapeHtml(s.current_gear)}</div>
                                </td>
                                <td style="padding: 1.5rem 1rem; vertical-align: top;">
                                    ${cleanContext 
                                        ? `<div style="font-size: 0.9rem; color: rgba(255,255,255,0.75); line-height: 1.6; max-height: 220px; overflow-y: auto;">${cleanContext}</div>` 
                                        : `<div style="font-size: 0.85rem; color: rgba(255,255,255,0.3); font-style: italic;">No context established</div>`}
                                </td>
                                <td style="padding: 1.5rem 1rem; vertical-align: top; min-width: 250px;">
                                    ${synopsisHtml}
                                </td>
                                <td style="padding: 1.5rem 1rem; vertical-align: top; font-size: 0.8rem; color: rgba(255,255,255,0.5); white-space: nowrap;">
                                    ${new Date(s.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </td>
                            </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
            `;
            
            container.innerHTML = tableHtml;
            this.setStatus(`${sessions.length} active session${sessions.length === 1 ? '' : 's'} loaded.`);

        } catch (error: any) {
            console.error('[AdminPortal] Error loading sessions:', error);
            this.setStatus(error.message || 'Error loading dashboard data.', 'error');
            const container = this.element.querySelector('#admin-content');
            if (container) {
                container.innerHTML = `<div style="color: #ff6b6b; padding: 2rem;">${escapeHtml(error.message || 'Error loading dashboard data.')}</div>`;
            }
        }
    }
}
